import type { ProjectPlanData } from '@platform/client/schemas';
import { Button } from '@platform/components';

type FinalPlanSectionProps = {
  planData: ProjectPlanData;
  savedDate: string;
  onEditPlan: () => void;
};

const SectionCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className='flex flex-col gap-3 rounded-xl bg-white p-4'>
    <span className="text-base font-semibold leading-6 text-gray-700">{title}</span>
    {children}
  </div>
);

const DataField = ({ label, value }: { label: string; value?: string | null }) => {
  if (!value) return null;
  return (
    <div className='flex flex-col gap-1'>
      <span className="text-xs font-medium leading-[18px] text-gray-400">{label}</span>
      <span className="text-sm font-normal leading-5 text-gray-700">{value}</span>
    </div>
  );
};

export const FinalPlanSection = ({ planData, savedDate, onEditPlan }: FinalPlanSectionProps) => {
  const hasTimeline = planData.startDate || planData.endDate || planData.availabilityNotes;
  const projectHubHref =
    planData.isTeam && planData.projectId != null
      ? `/platform/team-project-hub/${planData.projectId}`
      : planData.assignmentDiscoveryId != null
        ? `/platform/project-hub/${planData.assignmentDiscoveryId}`
        : undefined;
  const teamTasks =
    planData.isTeam && planData.teamMembers?.length
      ? planData.teamMembers
        .map((m) => {
          const task = planData.tasks?.find((t) => t.userId === m.id)?.task ?? '';
          return { ...m, task };
        })
        .filter((m) => m.task.trim().length > 0)
      : [];

  return (
    <div className='flex flex-col gap-4'>
      {/* Project Timeline */}
      {hasTimeline && (
        <SectionCard title='Project Timeline'>
          <div className='flex flex-wrap gap-6'>
            <DataField label='Start Date' value={planData.startDate?.substring(0, 10)} />
            <DataField label='End Date' value={planData.endDate?.substring(0, 10)} />
          </div>
          <DataField label='Unavailability Periods' value={planData.availabilityNotes} />
        </SectionCard>
      )}

      {/* Deliverables */}
      {planData.deliverables.length > 0 && (
        <SectionCard title='Deliverables'>
          <div className='flex flex-col gap-4'>
            {planData.deliverables.map((d) => (
              <div key={d.id} className='flex flex-col gap-1 border-l-2 border-l-brand-500 pl-4'>
                <span className="text-sm font-medium leading-5 text-gray-700">
                  {d.deliverable}
                </span>
                {d.context && (
                  <span className="text-sm font-normal leading-5 text-gray-500">
                    {d.context}
                  </span>
                )}
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Initial Task Distribution (team only) */}
      {teamTasks.length > 0 && (
        <SectionCard title='Initial Task Distribution'>
          <div className='flex flex-col gap-4'>
            {teamTasks.map((m) => (
              <div key={m.id} className='flex flex-col gap-1 border-l-2 border-l-brand-500 pl-4'>
                <span className="text-sm font-medium leading-5 text-gray-700">
                  {m.firstname} {m.lastname}
                </span>
                <span className="text-sm font-normal leading-5 text-gray-500 whitespace-pre-wrap">
                  {m.task}
                </span>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Communication Plan */}
      {planData.communicationNotes && (
        <SectionCard title='Communication Plan'>
          <span className="text-sm font-normal leading-5 text-gray-700 whitespace-pre-wrap">
            {planData.communicationNotes}
          </span>
        </SectionCard>
      )}

      {/* Next Steps */}
      {planData.nextSteps && (
        <SectionCard title='Next Steps & Action Items'>
          <span className="text-sm font-normal leading-5 text-gray-700 whitespace-pre-wrap">
            {planData.nextSteps}
          </span>
        </SectionCard>
      )}

      {/* Footer */}
      <div className='flex flex-wrap items-center gap-4'>
        {projectHubHref ?
          <Button variant='primary' size='sm' href={projectHubHref}>
            Back to Project Hub
          </Button>
          : <Button variant='primary' size='sm' onPress={() => window.history.back()}>
            Back to Project Hub
          </Button>
        }
        <Button
          variant='secondary-gray'
          size='sm'
          leadIcon='edit-04'
          leadIconClassNames='stroke-gray-500'
          onPress={onEditPlan}
        >
          Edit this plan
        </Button>
        {savedDate && (
          <span className="text-xs font-normal leading-[18px] text-gray-400">
            Last saved {savedDate}
          </span>
        )}
      </div>
    </div>
  );
};
